import { QueryClient } from '@tanstack/react-query';
import { debugLogger } from '@/utils/debug';

/**
 * Comprehensive cache invalidation utility for review-related data
 */
export const invalidateReviewCaches = async (
  queryClient: QueryClient, 
  providerId: string,
  options: {
    clearLocalStorage?: boolean;
    forceRefetch?: boolean;
    debugMode?: boolean;
  } = {}
) => {
  const { clearLocalStorage = false, forceRefetch = true, debugMode = true } = options;
  
  if (debugMode) {
    debugLogger.log(`Starting comprehensive cache invalidation for provider ${providerId}`);
  }

  try {
    // 1. Invalidate all serviceReviews queries for this provider
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const matches = query.queryKey[0] === 'serviceReviews' && query.queryKey[1] === providerId;
        if (matches && debugMode) {
          debugLogger.log(`Invalidating cache with key:`, query.queryKey);
        }
        return matches;
      }
    });

    // 2. Invalidate related review caches
    await queryClient.invalidateQueries({
      queryKey: ['allProviderReviews']
    });

    await queryClient.invalidateQueries({
      queryKey: ['adminReviews']
    });

    // 3. Remove any serviceReviews entries from the cache completely
    queryClient.removeQueries({
      predicate: (query) => {
        return query.queryKey[0] === 'serviceReviews' && query.queryKey[1] === providerId;
      }
    });

    // 4. Force refetch if requested
    if (forceRefetch) {
      await queryClient.refetchQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'serviceReviews' && query.queryKey[1] === providerId;
        },
        type: 'active' // Only refetch active queries
      });
    }

    // 5. Clear local storage if requested
    if (clearLocalStorage) {
      // Clear any potential cached data in localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('serviceReviews') || key.includes(providerId))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (debugMode && keysToRemove.length > 0) {
        debugLogger.log(`Cleared localStorage keys:`, keysToRemove);
      }
    }

    if (debugMode) {
      debugLogger.log(`Cache invalidation completed for provider ${providerId}`);
      
      // Log current cache state
      const currentQueries = queryClient.getQueryCache().getAll();
      const serviceReviewQueries = currentQueries.filter(q => 
        q.queryKey[0] === 'serviceReviews' && q.queryKey[1] === providerId
      );
      debugLogger.log(`Current serviceReviews queries for provider ${providerId}:`, 
        serviceReviewQueries.map(q => ({ 
          key: q.queryKey, 
          state: q.state.status,
          dataUpdatedAt: q.state.dataUpdatedAt 
        }))
      );
    }

  } catch (error) {
    console.error('Error during cache invalidation:', error);
    throw error;
  }
};

/**
 * Force a hard refresh of review data by clearing all caches and refetching
 */
export const forceReviewDataRefresh = async (queryClient: QueryClient, providerId: string) => {
  await invalidateReviewCaches(queryClient, providerId, {
    clearLocalStorage: true,
    forceRefetch: true,
    debugMode: true
  });
  
  // Additional aggressive cache clearing
  queryClient.clear();
  
  // Trigger a small delay to ensure cache is fully cleared
  await new Promise(resolve => setTimeout(resolve, 100));
  
  debugLogger.log('Hard refresh completed - all caches cleared');
};
